import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/style_orderConfirmation.css";

// Configuration des créneaux horaires
const TIME_SLOTS = {
  morning: { label: "Matin", description: "9h - 12h" },
  early_afternoon: { label: "Début d'après-midi", description: "12h - 15h" },
  late_afternoon: { label: "Fin d'après-midi", description: "15h - 18h" },
  evening: { label: "Soirée", description: "18h - 20h" },
};

export default function OrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  // Récupérer les données de la commande depuis le state de navigation
  const orderData = location.state?.orderData;
  const orderNumber = location.state?.orderNumber || `FL-${Date.now().toString(36).toUpperCase()}`;

  useEffect(() => {
    // Si pas de données de commande, rediriger vers l'accueil
    if (!orderData) {
      navigate("/");
      return;
    }

    // Animation d'entrée
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 800);

    // Arrêter les confettis après quelques secondes
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, [orderData, navigate]);

  // Formater le prix
  const formatPrice = (price) => price?.toFixed(2).replace(".", ",") || "0,00";

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (!orderData) return null;

  return (
    <div className="confirmation-page">
      {/* Confettis floraux */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className={`confetti confetti-${i % 6}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              {["🌸", "🌺", "🌷", "🌹", "💐", "🌻"][i % 6]}
            </div>
          ))}
        </div>
      )}

      {/* Animation de succès */}
      <div className="success-animation">
        <div className="success-circle">
          <div className="success-checkmark">
            <svg viewBox="0 0 52 52">
              <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
              <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
          <div className="flower-burst">
            {[...Array(8)].map((_, i) => (
              <span key={i} className="flower-petal" style={{ "--i": i }}>
                🌸
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className={`confirmation-content ${showContent ? "visible" : ""}`}>
        <div className="confirmation-header">
          <h1>Merci pour votre commande !</h1>
          <p className="order-number">
            Commande n° <strong>{orderNumber}</strong>
          </p>
          <p className="confirmation-message">
            Votre commande a été confirmée avec succès. 
            Vous recevrez un email de confirmation sous peu.
          </p>
        </div>

        {/* Récapitulatif */}
        <div className="confirmation-recap">
          {/* Livraison */}
          <div className="recap-card">
            <div className="recap-card-header">
              <span className="recap-icon">
                {orderData.deliveryMode === "delivery" ? "🚚" : "🏪"}
              </span>
              <h3>
                {orderData.deliveryMode === "delivery" 
                  ? "Livraison à domicile" 
                  : "Retrait en boutique"}
              </h3>
            </div>
            <div className="recap-card-body">
              {orderData.deliveryMode === "delivery" && orderData.deliveryAddress && (
                <p className="address-text">
                  {orderData.deliveryAddress.street_name}<br />
                  {orderData.deliveryAddress.complement && (
                    <>{orderData.deliveryAddress.complement}<br /></>
                  )}
                  {orderData.deliveryAddress.postal_code} {orderData.deliveryAddress.city}
                </p>
              )}
              {orderData.deliveryMode === "click_collect" && (
                <p className="address-text">
                  L'Atelier Floral<br />
                  12 rue des Jardins, 75001 Paris
                </p>
              )}
            </div>
          </div>

          {/* Date et heure */}
          <div className="recap-card">
            <div className="recap-card-header">
              <span className="recap-icon">📅</span>
              <h3>Date & Créneau</h3>
            </div>
            <div className="recap-card-body">
              <p className="date-text">
                {formatDate(orderData.deliveryDate)}
              </p>
              <p className="time-text">
                {TIME_SLOTS[orderData.timeSlot]?.label} ({TIME_SLOTS[orderData.timeSlot]?.description})
              </p>
            </div>
          </div>

          {/* Articles */}
          <div className="recap-card recap-card-full">
            <div className="recap-card-header">
              <span className="recap-icon">🌷</span>
              <h3>Vos articles ({orderData.items?.length || 0})</h3>
            </div>
            <div className="recap-card-body">
              <div className="items-list">
                {orderData.items?.map((item, index) => (
                  <div key={index} className="item-row">
                    <span className="item-qty">{item.quantity}x</span>
                    <span className="item-name">
                      {item.productName || `Produit #${item.productId}`}
                      <small> ({item.variantLabel || item.variantCode})</small>
                    </span>
                    <span className="item-price">
                      {formatPrice(item.unitPrice * item.quantity)} €
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Options */}
          {(orderData.extras?.length > 0 || orderData.labels?.length > 0) && (
            <div className="recap-card">
              <div className="recap-card-header">
                <span className="recap-icon">🎁</span>
                <h3>Options</h3>
              </div>
              <div className="recap-card-body">
                <ul className="options-list">
                  {orderData.extras?.map((extra, index) => (
                    <li key={index}>
                      {extra.type === "ribbon" && "🎀 Ruban personnalisé"}
                      {extra.type === "card" && "💌 Carte message"}
                      {extra.type === "gift_wrap" && "🎁 Emballage premium"}
                      {extra.type === "vase" && "🏺 Vase assorti"}
                      {extra.text && <span className="option-text">: "{extra.text}"</span>}
                    </li>
                  ))}
                  {orderData.labels?.length > 0 && (
                    <li>🏷️ {orderData.labels.length} étiquette{orderData.labels.length > 1 ? "s" : ""}</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {/* Total */}
          <div className="recap-card recap-card-total">
            <div className="total-breakdown">
              <div className="total-row">
                <span>Sous-total articles</span>
                <span>{formatPrice(orderData.totals?.productsSubtotal)} €</span>
              </div>
              {orderData.totals?.deliveryFee > 0 && (
                <div className="total-row">
                  <span>Livraison</span>
                  <span>{formatPrice(orderData.totals?.deliveryFee)} €</span>
                </div>
              )}
              {orderData.totals?.deliveryFee === 0 && orderData.deliveryMode === "delivery" && (
                <div className="total-row free">
                  <span>Livraison</span>
                  <span>Offerte</span>
                </div>
              )}
              {orderData.totals?.extrasTotal > 0 && (
                <div className="total-row">
                  <span>Options</span>
                  <span>+{formatPrice(orderData.totals?.extrasTotal)} €</span>
                </div>
              )}
              {orderData.totals?.labelsTotal > 0 && (
                <div className="total-row">
                  <span>Étiquettes</span>
                  <span>+{formatPrice(orderData.totals?.labelsTotal)} €</span>
                </div>
              )}
              <div className="total-row total-final">
                <span>Total payé</span>
                <span>{formatPrice(orderData.totals?.total)} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="confirmation-actions">
          <Link to="/boutique" className="btn-continue">
            <ion-icon name="leaf-outline"></ion-icon>
            Continuer mes achats
          </Link>
          <Link to="/" className="btn-home">
            <ion-icon name="home-outline"></ion-icon>
            Retour à l'accueil
          </Link>
        </div>

        {/* Message de suivi */}
        <div className="tracking-info">
          <ion-icon name="mail-outline"></ion-icon>
          <p>
            Un email de confirmation a été envoyé à votre adresse. 
            Vous pourrez suivre l'état de votre commande depuis votre espace client.
          </p>
        </div>
      </div>

      {/* Fleurs décoratives */}
      <div className="deco-flowers">
        <span className="deco-flower deco-1">🌸</span>
        <span className="deco-flower deco-2">🌺</span>
        <span className="deco-flower deco-3">🌷</span>
        <span className="deco-flower deco-4">🌹</span>
      </div>
    </div>
  );
}
